import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { computeOFTECFlags } from './validator';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 25,
    fontFamily: 'Helvetica',
    fontSize: 8
  },
  header: {
    marginBottom: 12,
    textAlign: 'center',
    borderBottom: 2,
    paddingBottom: 8
  },
  title: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 4
  },
  section: {
    marginBottom: 8,
    border: 1,
    padding: 6
  },
  sectionTitle: {
    fontSize: 9,
    fontWeight: 'bold',
    marginBottom: 4,
    textAlign: 'center',
    backgroundColor: '#d0d0d0',
    padding: 3,
    marginLeft: -6,
    marginRight: -6,
    marginTop: -6
  },
  row: {
    flexDirection: 'row',
    marginBottom: 3
  },
  label: {
    fontSize: 7,
    fontWeight: 'bold',
    width: '30%'
  },
  value: {
    fontSize: 7,
    width: '70%'
  },
  signatureBox: {
    marginTop: 8,
    padding: 4,
    borderBottom: 1,
    borderColor: '#000',
    minHeight: 35
  },
  signatureImage: {
    maxHeight: 30,
    maxWidth: 180,
    objectFit: 'contain'
  },
  footer: {
    marginTop: 8,
    borderTop: 1,
    paddingTop: 4
  },
  footerText: {
    fontSize: 5,
    textAlign: 'center'
  }
});

const OilFiringInstallationPDF = ({ formData }) => {
  const oftec = computeOFTECFlags(formData);
  const hasCombustion = oftec.efficiency !== null;
  const tankCheck = oftec.tankCheck;

  return (
    <Document>
      <Page size="A4" style={styles.page} orientation="portrait">
        <View style={styles.header}>
          <Text style={styles.title}>Oil Firing Installation Completion Report</Text>
        </View>

        {hasCombustion && oftec.combustionResult && (
          <View style={[styles.section, { backgroundColor: oftec.combustionResult.pass ? '#e6f7e6' : '#ffe9a8' }]}>
            <Text style={[styles.sectionTitle, { backgroundColor: '#3c763d', color: '#FFFFFF' }]}>
              COMBUSTION TEST RESULTS (OFTEC / BS 5410)
            </Text>
            <View style={styles.row}>
              <Text style={styles.label}>Net Efficiency:</Text>
              <Text style={styles.value}>{oftec.efficiency.netEfficiencyPct}%</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Gross Efficiency:</Text>
              <Text style={styles.value}>{oftec.efficiency.grossEfficiencyPct}%</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Excess Air:</Text>
              <Text style={styles.value}>{oftec.efficiency.excessAir}%</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Smoke Number:</Text>
              <Text style={styles.value}>{oftec.combustionResult.smokeNumber}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>CO:</Text>
              <Text style={styles.value}>{oftec.combustionResult.coPpm} ppm</Text>
            </View>
            {!oftec.combustionResult.pass && (
              <Text style={{ fontSize: 6, color: '#B22222', marginTop: 3 }}>
                ⚠ Combustion not satisfactory: {oftec.combustionResult.issues.join(' ')}
              </Text>
            )}
          </View>
        )}

        {tankCheck && (
          <View style={[styles.section, { backgroundColor: tankCheck.compliant ? '#e6f7e6' : '#ffe9a8' }]}>
            <Text style={[styles.sectionTitle, { backgroundColor: '#3c763d', color: '#FFFFFF' }]}>
              TANK SEPARATION COMPLIANCE (BS 5410)
            </Text>
            <View style={styles.row}>
              <Text style={styles.label}>Distance to Building:</Text>
              <Text style={styles.value}>{tankCheck.distanceToBuildingM} m</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Distance to Boundary:</Text>
              <Text style={styles.value}>{tankCheck.distanceToBoundaryM} m</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Capacity:</Text>
              <Text style={styles.value}>{tankCheck.capacityLitres} L</Text>
            </View>
            {!tankCheck.compliant && (
              <Text style={{ fontSize: 6, color: '#B22222', marginTop: 3 }}>
                ⚠ {tankCheck.issues.join(' ')}
              </Text>
            )}
          </View>
        )}

        {/* NOTE: Customize the sections below based on your certificate structure */}
        {/* This is a basic template - you can manually edit after generation */}
        
        {Object.keys(formData).map((sectionKey) => {
          const sectionData = formData[sectionKey];
          if (!sectionData || typeof sectionData !== 'object') return null;
          
          return (
            <View key={sectionKey} style={styles.section}>
              <Text style={styles.sectionTitle}>{sectionKey.toUpperCase()}</Text>
              {Object.entries(sectionData).map(([fieldKey, fieldValue]) => {
                if (!fieldValue) return null;
                
                // Handle signatures
                if (typeof fieldValue === 'string' && fieldValue.startsWith('data:image')) {
                  return (
                    <View key={fieldKey} style={styles.signatureBox}>
                      <Text style={styles.label}>{fieldKey}:</Text>
                      <Image src={fieldValue} style={styles.signatureImage} />
                    </View>
                  );
                }
                
                // Handle regular fields
                return (
                  <View key={fieldKey} style={styles.row}>
                    <Text style={styles.label}>{fieldKey}:</Text>
                    <Text style={styles.value}>{String(fieldValue)}</Text>
                  </View>
                );
              })}
            </View>
          );
        })}

        <View style={styles.footer}>
          <Text style={styles.footerText}>
            Generated: {new Date().toLocaleDateString('en-GB')}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default OilFiringInstallationPDF;
